import { Box, createStyles, Stack } from '@mantine/core';

import { SettingsSelect, SettingsSwitch } from '@repo/common/components';
import { Text, Title } from '@repo/foundations';
import { useState } from 'react';
import { useAuthUser } from '../../api';
import { useSuspenseFeatureAccess } from '../../api/hooks/workspace/useSuspenseFeatureAccess';
import { useUpdateWorkspace } from '../../api/hooks/workspace/useUpdateWorkspace';
import { useFeatureFlags } from '../../utils/featureFlags';
import { EmptyState } from '../EmptyState';
import { UpgradeButton } from './UpgradeButton';

const useStyles = createStyles((theme) => ({
	root: {
		gap: theme.spacing['4xl'],
	},
	input: {
		border: '0',
		background: theme.other.getColor('fill/transparent/default'),
	},
}));

type PolicyIntelligenceMode = 'LLM (Recommended)' | 'Local';

export function PoliciesSettings() {
	const { workspace } = useAuthUser();
	const { mutateAsync: updateWorkspace } = useUpdateWorkspace(workspace.id);
	const { policiesAccess } = useSuspenseFeatureAccess();
	const { dataScanning } = useFeatureFlags();
	const { classes } = useStyles();

	// State for policy intelligence mode
	const [policyIntelligenceMode, setPolicyIntelligenceMode] =
		useState<PolicyIntelligenceMode>(
			workspace.data_scanning_llm_enabled ? 'LLM (Recommended)' : 'Local'
		);

	// Model options for policy intelligence mode
	const modelOptions: { value: string; label: PolicyIntelligenceMode }[] = [
		{ value: 'LLM (Recommended)', label: 'LLM (Recommended)' },
		{ value: 'Local', label: 'Local' },
	];

	const handlePolicyIntelligenceModeChange = (value: string) => {
		setPolicyIntelligenceMode(
			modelOptions.find((option) => option.value === value)?.label ??
				'LLM (Recommended)'
		);
		updateWorkspace({
			data: {
				id: workspace.id,
				data_scanning_llm_enabled: value === 'LLM (Recommended)',
			},
		});
	};

	if (!policiesAccess) {
		return (
			<EmptyState
				title="Upgrade to access Policies"
				description="You can use Policies to define and enforce data governance rules across your organization"
				illustrationName="upgrade"
				includeGoBack={false}
				stateHeight="50vh"
				size="lg"
				withActions={<UpgradeButton feature="Policies" size="md" />}
			/>
		);
	}

	return (
		<Stack className={classes.root}>
			<Box>
				<SettingsSwitch
					onChange={(checked) =>
						updateWorkspace({
							data: { id: workspace.id, policies_enabled: checked },
						})
					}
					checked={workspace?.policies_enabled ?? false}
					title="Policies"
					description="Turn on Policies for this workspace"
				/>
			</Box>

			{dataScanning && (
				<Stack spacing="md">
					<Stack spacing={0}>
						<Title order={4} size="md" weight="bold">
							Policy intelligence mode
						</Title>
						<Text size="sm" color="text/secondary/default">
							LLM-first for smarter automation, or local for greater control and
							privacy
						</Text>
					</Stack>

					<SettingsSelect
						title="Model"
						description="Select how policy evaluation runs in your workspace"
						options={modelOptions}
						onChange={(value) =>
							handlePolicyIntelligenceModeChange(value ?? 'LLM (Recommended)')
						}
						searchable={false}
						value={policyIntelligenceMode}
						ignoreFirstOfType={true}
					/>
				</Stack>
			)}
		</Stack>
	);
}
